import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    makeStyles,
    mergeClasses,
    Spinner,
    Text,
    tokens,
} from '@fluentui/react-components';
import { ArrowDownloadRegular, CopyRegular, DismissCircle24Regular } from '@fluentui/react-icons';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProblemDetails } from '../../../model/problemDetails';

export type Pillar2DataPreviewStatus = 'loading' | 'success' | 'error';

export interface IPillar2DataPreviewDialogProps {
    open: boolean;
    onClose: () => void;
    status: Pillar2DataPreviewStatus;
    content?: string;
    contentType?: string | null;
    taxReturnName?: string | null;
    errorDetails?: ProblemDetails | unknown;
}

function parseJsonValue(value: unknown): unknown {
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
            try {
                return JSON.parse(trimmed);
            } catch {
                return value;
            }
        }
        return value;
    }
    if (value && typeof value === 'object' && 'response' in value) {
        const data = (value as { response?: { data?: unknown } }).response?.data;
        if (data !== undefined && data !== null) {
            return parseJsonValue(data);
        }
    }
    return value;
}

function formatErrorDetails(details: unknown): string {
    if (details === undefined || details === null) {
        return '';
    }
    const parsed = parseJsonValue(details);
    try {
        return JSON.stringify(parsed, null, 2);
    } catch {
        return String(details);
    }
}

const useStyles = makeStyles({
    surface: {
        width: '720px',
        maxWidth: '90vw',
    },
    pre: {
        backgroundColor: tokens.colorNeutralBackground2,
        padding: '12px',
        borderRadius: '4px',
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        fontFamily: tokens.fontFamilyMonospace,
        fontSize: '12px',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
        maxHeight: '60vh',
        overflowY: 'auto',
        margin: '0',
    },
    spinnerWrapper: {
        display: 'flex',
        justifyContent: 'center',
        padding: '32px 0',
    },
    errorMessageRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        marginBottom: tokens.spacingVerticalL,
    },
    errorIcon: {
        flexShrink: 0,
        fontSize: '24px',
        lineHeight: '24px',
        color: tokens.colorPaletteRedForeground1,
    },
    errorMessageText: {
        flex: 1,
        margin: 0,
        lineHeight: tokens.lineHeightBase300,
        color: tokens.colorNeutralForeground1,
    },
    errorDetailsSection: {
        marginTop: tokens.spacingVerticalM,
    },
    errorDetailsLabel: {
        display: 'block',
        marginBottom: tokens.spacingVerticalXS,
        fontWeight: tokens.fontWeightSemibold,
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '8px',
    },
});

export const Pillar2DataPreviewDialog: FC<IPillar2DataPreviewDialogProps> = ({
    open,
    onClose,
    status,
    content,
    contentType,
    errorDetails,
    taxReturnName,
}) => {
    const { t } = useTranslation();
    const styles = useStyles();
    const [copied, setCopied] = useState(false);

    const errorDetailsText = useMemo(() => formatErrorDetails(errorDetails), [errorDetails]);

    const handleCopy = () => {
        if (!content) return;
        void navigator.clipboard.writeText(content).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    };

    const handleDownload = () => {
        if (!(content && contentType && taxReturnName)) return;

        const contentBlob: Blob = new Blob([content], { type: `text/${contentType.toLowerCase()}` });
        const blobDownloadUrl: string = window.URL.createObjectURL(contentBlob);

        try {
            const downloadLink: HTMLAnchorElement = document.createElement('a');

            downloadLink.href = blobDownloadUrl;
            downloadLink.download = `${taxReturnName}.${contentType.toLowerCase()}`;
            downloadLink.style.display = 'none';
            document.body.appendChild(downloadLink);

            try {
                downloadLink.click();
            } finally {
                document.body.removeChild(downloadLink);
            }
        } finally {
            window.URL.revokeObjectURL(blobDownloadUrl);
        }
    };

    return (
        <Dialog open={open} modalType='alert' onOpenChange={() => {}}>
            <DialogSurface className={styles.surface}>
                <DialogBody>
                    <DialogTitle>{t('pillar2ReturnPreviewTitle')}</DialogTitle>
                    <DialogContent>
                        {status === 'loading' && (
                            <div className={styles.spinnerWrapper}>
                                <Spinner label={t('pillar2ReturnPreviewLoading').toString()} />
                            </div>
                        )}
                        {status === 'error' && (
                            <>
                                <div className={styles.errorMessageRow}>
                                    <DismissCircle24Regular className={styles.errorIcon} aria-hidden />
                                    <Text className={styles.errorMessageText}>{t('pillar2ReturnPreviewRetrieveFailed').toString()}</Text>
                                </div>
                                {errorDetailsText ? (
                                    <div className={styles.errorDetailsSection}>
                                        <Text className={styles.errorDetailsLabel}>{t('pillar2ReturnPreviewErrorDetails').toString()}</Text>
                                        <pre
                                            className={mergeClasses(styles.pre, 'ph-no-capture')}
                                            aria-label={t('pillar2ReturnPreviewErrorDetails').toString()}
                                        >
                                            {errorDetailsText}
                                        </pre>
                                    </div>
                                ) : null}
                            </>
                        )}
                        {status === 'success' && (
                            <pre
                                className={mergeClasses(styles.pre, 'ph-no-capture')}
                                aria-label={t('pillar2ReturnPreviewTitle').toString()}
                            >
                                {content ?? t('pillar2ReturnPreviewEmpty').toString()}
                            </pre>
                        )}
                    </DialogContent>
                    <DialogActions className={styles.actions}>
                        {status === 'success' && content && (
                            <>
                                <Button
                                    appearance='secondary'
                                    icon={<CopyRegular />}
                                    onClick={handleCopy}
                                    aria-label={
                                        copied ? t('pillar2ReturnPreviewCopied').toString() : t('pillar2ReturnPreviewCopy').toString()
                                    }
                                >
                                    {copied ? t('pillar2ReturnPreviewCopied').toString() : t('pillar2ReturnPreviewCopy').toString()}
                                </Button>
                                <Button
                                    appearance='secondary'
                                    icon={<ArrowDownloadRegular />}
                                    onClick={handleDownload}
                                    aria-label={t('download').toString()}
                                >
                                    {t('download').toString()}
                                </Button>
                            </>
                        )}
                        <Button appearance='secondary' onClick={onClose} aria-label={t('close').toString()}>
                            {t('close')}
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};
